import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConnectorBindingConfiguration } from '../useConfigurationForms';
import { useMenuConfiguration } from '../../../stores/useConfigurationStore';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';
import { useSnackbarStore } from '../../../stores/useSnackbarStore';
import { useRevitData } from '../../../providers/RevitDataContext';
import {
  type PendingRevitOperationV2,
  type RevitOperationProgressSnapshot,
} from '../../../api/grpc/operationProgressClient';
import {
  ensureE2ePendingConnectorOperationSeeded,
  resolveOperationProgressFetch,
} from '../../../config/e2ePendingConnectorFixture';
import { resumeAddParametersToSpecificFamilies } from '../../../api/grpc/familyClient';
import { useActiveDocumentInfoContext } from '../../../hooks/ActiveDocumentProvider';
import { invalidateFamilySnapshotCache } from '../../revit/familySnapshotCache';
import { useUIStore } from '../../../stores/useUIStore';
import { useBatchOperation } from '../../batchOperations';
import { mapRevitOperationSnapshotToProgressModel, type OperationProgressModel } from '../../../ui/progress';
import { fetchRevitFamiliesSnapshotResult } from './availability';
import { collectConfiguredParameters } from './configuration';
import {
  buildExpectedDocumentIdentity,
  describeConnectorExecutionMode,
  resolveProjectUndoKey,
} from './operationIdentity';
import {
  mapResumedFamilyParameterResult,
  type ActiveConnectorOperationUiState,
  type ParameterAdditionResult,
} from './models';
import { type ConnectorParameterInFlightEntry } from './pendingConnectorRecovery';

const PendingOperationTrackIntervalMs = 1_000;

const PendingOperationTrackTimeoutMs = 5 * 60_000;

const waitMs = (durationMs: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, durationMs);
  });

export function useParameterAdditionCore() {
  const [isAdding, setIsAdding] = useState(false);

  const [parameterProgress, setParameterProgress] = useState<
    OperationProgressModel | null
  >(null);

  const [activeConnectorOperation, setActiveConnectorOperation] = useState<
    ActiveConnectorOperationUiState | null
  >(null);

  const [connectorSelectionBlockedByMismatch, setConnectorSelectionBlockedByMismatch] =
    useState(false);

  const additionalCategoryBatchOperation = useBatchOperation();

  // Get configuration from stores
  const { config: connectorConfig } = useConnectorBindingConfiguration();

  const menuConfig = useMenuConfiguration();

  const processConfig = useConfigurationStore((state) => state.processPropertiesConfiguration);

  const boltConfig = useConfigurationStore((state) => state.boltConfiguration);

  const additionalCategoriesConfig = useConfigurationStore(
    (state) => state.additionalCategoriesConfiguration,
  );

  const lastConnectorUndoPlan = useUIStore((state) => state.connectorParameterUndoPlan);

  const setConnectorParameterUndoPlan = useUIStore((state) => state.setConnectorParameterUndoPlan);

  const lastAdditionalCategoryUndoPlan = useUIStore(
    (state) => state.additionalCategoryParameterUndoPlan,
  );

  const setAdditionalCategoryParameterUndoPlan = useUIStore(
    (state) => state.setAdditionalCategoryParameterUndoPlan,
  );

  const connectorExecutionModeDescription = describeConnectorExecutionMode(
    connectorConfig.parameterDestination,
    connectorConfig.parameterBindingKind,
  );

  // Snackbar for feedback
  const { showSuccess, showError, showWarning, showInfo } = useSnackbarStore();

  // Revit settings identify the active project for scoped flow caches.
  const { settings } = useRevitData();

  const activeDocumentInfo = useActiveDocumentInfoContext();

  const expectedDocumentIdentity = useMemo(
    () =>
      buildExpectedDocumentIdentity({
        documentName: activeDocumentInfo.documentName,
        documentPath: activeDocumentInfo.documentPath,
      }),
    [activeDocumentInfo.documentName, activeDocumentInfo.documentPath],
  );

  const currentProjectUndoKey = resolveProjectUndoKey(
    activeDocumentInfo,
    settings,
  );

  const currentProjectUndoKeyRef = useRef(currentProjectUndoKey);

  const connectorKnownFamilyKeysRef = useRef<
    {
      projectKey: string;
      familyKeys: Set<string>;
    } | null
  >(null);

  const addParametersInFlightRef = useRef<ConnectorParameterInFlightEntry | null>(null);

  const addAdditionalCategoryParametersInFlightRef = useRef<
    Promise<ParameterAdditionResult> | null
  >(null);

  const addProcessAndProjectParametersInFlightRef = useRef<
    Promise<ParameterAdditionResult> | null
  >(null);

  const startParameterProgress = useCallback(
    (
      model:
        & Pick<OperationProgressModel, 'title' | 'message'>
        & Partial<OperationProgressModel>,
    ) => {
      setParameterProgress({
        status: 'running',
        percent: null,
        locked: true,
        ...model,
      });
    },
    [],
  );

  const updateParameterProgressFromHost = useCallback(
    (progress: RevitOperationProgressSnapshot) => {
      setParameterProgress((current) =>
        mapRevitOperationSnapshotToProgressModel(progress, {
          title: current?.title ?? 'Working in Revit',
          message: 'Revit is processing the request',
          completeMessage: current?.message || 'Revit operation completed',
          errorMessage: 'Revit operation failed',
          metrics: current?.metrics,
          locked: !progress.resultReady && !progress.failed,
        })
      );
    },
    [],
  );

  useEffect(() => {
    currentProjectUndoKeyRef.current = currentProjectUndoKey;
    if (
      currentProjectUndoKey &&
      connectorKnownFamilyKeysRef.current &&
      connectorKnownFamilyKeysRef.current.projectKey !== currentProjectUndoKey
    ) {
      connectorKnownFamilyKeysRef.current = null;
    }
  }, [currentProjectUndoKey]);

  // A recovered backend operation lives in its own slot: a mismatched or
  // foreign recovery must never occupy the connector in-flight entry.
  const recoveredOperationRef = useRef<Promise<ParameterAdditionResult> | null>(null);

  // The e2e screenshot lane substitutes progress probes for its seeded
  // fixture operation; outside that lane this is the real gRPC fetch.
  const fetchOperationProgress = useMemo(() => resolveOperationProgressFetch(), []);

  useEffect(() => {
    ensureE2ePendingConnectorOperationSeeded();
  }, []);

  const probePendingOperation = useCallback(
    async (operationId: string): Promise<'live' | 'terminal' | 'lost'> => {
      const probe = await fetchOperationProgress(operationId);
      if (!probe.success || !probe.progress) {
        return 'lost';
      }

      return probe.progress.failed || probe.progress.resultReady ? 'terminal' : 'live';
    },
    [fetchOperationProgress],
  );

  const trackPendingOperationToTerminal = useCallback(
    async (operationId: string): Promise<{ terminal: boolean; lost: boolean }> => {
      const deadline = Date.now() + PendingOperationTrackTimeoutMs;
      for (;;) {
        const probe = await fetchOperationProgress(operationId);
        if (!probe.success || !probe.progress) {
          return { terminal: false, lost: true };
        }
        if (probe.progress.failed || probe.progress.resultReady) {
          return { terminal: true, lost: false };
        }
        if (Date.now() >= deadline) {
          return { terminal: false, lost: false };
        }
        await waitMs(PendingOperationTrackIntervalMs);
      }
    },
    [fetchOperationProgress],
  );

  const resumeExactPendingConnectorOperation = useCallback(
    async (record: PendingRevitOperationV2): Promise<ParameterAdditionResult> => {
      if (recoveredOperationRef.current) {
        return recoveredOperationRef.current;
      }

      setIsAdding(true);
      startParameterProgress({
        title: record.title,
        message: 'Revit is applying connector parameter setup',
        detail: record.connectorTarget
          ? describeConnectorExecutionMode(
            record.connectorTarget.destination,
            record.connectorTarget.bindingKind,
          )
          : undefined,
      });

      const operationPromise = (async (): Promise<ParameterAdditionResult> => {
        try {
          const resumed = await resumeAddParametersToSpecificFamilies(
            record.operationId,
            {
              onProgress: updateParameterProgressFromHost,
            },
          );
          if (!resumed.success) {
            if (!resumed.lost && resumed.error) {
              showWarning(resumed.error);
            }
            return {
              success: false,
              addedCount: 0,
              error: resumed.error ||
                'Pending family parameter operation is no longer available.',
            };
          }

          invalidateFamilySnapshotCache(currentProjectUndoKeyRef.current);
          const result = resumed.result ? mapResumedFamilyParameterResult(resumed.result) : {
            success: false,
            addedCount: 0,
            error: 'Pending family parameter operation returned no result.',
          };
          if (result.success) {
            showInfo(
              'Connector parameter request completed after the window was reloaded.',
            );
          } else {
            showWarning(result.error || 'Family parameter setup failed');
          }

          return result;
        } catch (error) {
          const message = error instanceof Error
            ? error.message
            : 'Failed to resume the pending family parameter operation.';
          showWarning(message);
          return {
            success: false,
            addedCount: 0,
            error: message,
          };
        } finally {
          setIsAdding(false);
          setParameterProgress(null);
        }
      })();
      recoveredOperationRef.current = operationPromise;
      void operationPromise.finally(() => {
        if (recoveredOperationRef.current === operationPromise) {
          recoveredOperationRef.current = null;
        }
      });
      return operationPromise;
    },
    [
      showInfo,
      showWarning,
      startParameterProgress,
      updateParameterProgressFromHost,
    ],
  );

  const didProjectChange = useCallback(
    (expectedProjectKey: string | null): boolean => {
      return expectedProjectKey !== currentProjectUndoKeyRef.current;
    },
    [],
  );

  useEffect(() => {
    if (!lastConnectorUndoPlan) {
      return;
    }

    if (
      !currentProjectUndoKey ||
      lastConnectorUndoPlan.projectKey !== currentProjectUndoKey
    ) {
      setConnectorParameterUndoPlan(null);
    }
  }, [
    currentProjectUndoKey,
    lastConnectorUndoPlan,
    setConnectorParameterUndoPlan,
  ]);

  useEffect(() => {
    if (!lastAdditionalCategoryUndoPlan) {
      return;
    }

    if (
      !currentProjectUndoKey ||
      lastAdditionalCategoryUndoPlan.projectKey !== currentProjectUndoKey
    ) {
      setAdditionalCategoryParameterUndoPlan(null);
    }
  }, [
    currentProjectUndoKey,
    lastAdditionalCategoryUndoPlan,
    setAdditionalCategoryParameterUndoPlan,
  ]);

  const getRevitFamiliesSnapshotResult = useCallback(
    async (specificParameterNames?: string[]) => {
      return fetchRevitFamiliesSnapshotResult(specificParameterNames, {
        cacheScopeKey: currentProjectUndoKeyRef.current,
      });
    },
    [],
  );

  /**
   * Collects all configured parameter names from stores
   */
  const getConfiguredParameters = useCallback((): string[] => {
    return collectConfiguredParameters({
      menuConfig,
      connectorConfig,
      processConfig,
      boltConfig,
      additionalCategoriesConfig,
    });
  }, [
    additionalCategoriesConfig,
    boltConfig,
    connectorConfig,
    menuConfig,
    processConfig,
  ]);

  return {
    isAdding,
    setIsAdding,
    parameterProgress,
    setParameterProgress,
    activeConnectorOperation,
    setActiveConnectorOperation,
    connectorSelectionBlockedByMismatch,
    setConnectorSelectionBlockedByMismatch,
    additionalCategoryBatchOperation,
    connectorConfig,
    menuConfig,
    processConfig,
    boltConfig,
    additionalCategoriesConfig,
    lastConnectorUndoPlan,
    setConnectorParameterUndoPlan,
    lastAdditionalCategoryUndoPlan,
    setAdditionalCategoryParameterUndoPlan,
    connectorExecutionModeDescription,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    settings,
    activeDocumentInfo,
    expectedDocumentIdentity,
    currentProjectUndoKey,
    currentProjectUndoKeyRef,
    connectorKnownFamilyKeysRef,
    addParametersInFlightRef,
    addAdditionalCategoryParametersInFlightRef,
    addProcessAndProjectParametersInFlightRef,
    startParameterProgress,
    updateParameterProgressFromHost,
    probePendingOperation,
    trackPendingOperationToTerminal,
    resumeExactPendingConnectorOperation,
    didProjectChange,
    getRevitFamiliesSnapshotResult,
    getConfiguredParameters,
  };
}

export type ParameterAdditionCore = ReturnType<typeof useParameterAdditionCore>;
